import { useCallback } from 'react';
import { unselect, useListNavigation, useListNavigationHandlers } from '@clds/compound-components';
import { MenuState, useChildRegistry, useConfirm, useDescendants, useMenuProviderProps, useMenuState } from '@clds/menu-old';
import { useDropdownMenu, useDropdownMenuState } from '@cld/dropdown-menu';
import { UseCustomMenuStateProps } from './useCustomMenuState.types';

export const useCustomMenu = (state: MenuState) => {
  const registry = useChildRegistry();
  const { ref, ids } = useDescendants();
  const confirm = useConfirm(registry, state);
  const navigationHandlers = useListNavigationHandlers({ activeItem: state.focusedId, onActiveItemChange: state.setFocusedId, items: ids });
  const onEnter = useCallback(() => {
    if (ids.length) {
      confirm();
    }
  }, [confirm, ids.length]);
  const { focusableProps } = useListNavigation({
    ...navigationHandlers,
    onEnter: onEnter,
  });
  const menuProviderProps = useMenuProviderProps(registry, state);

  return {
    descendantsParentRef: ref,
    focusableProps,
    menuProviderProps,
  };
};

export const useCustomMenuState = ({
  defaultOpened,
  isOpened,
  trigger,
  onOpenedChange,
  defaultSelectedId,
  selectedId,
  defaultFocusedId,
  focusedId,
  onFocusedIdChange,
  onSelect,
  items,
}: UseCustomMenuStateProps) => {
  const popoverSelectedId = defaultSelectedId === undefined && selectedId === undefined ? unselect : selectedId;

  const menuState = useMenuState({
    defaultFocusedId,
    focusedId,
    onFocusedIdChange,
    defaultSelectedId,
    selectedId: popoverSelectedId,
    onSelect,
  });
  const dropdownMenuState = useDropdownMenuState({ defaultOpened, isOpened, onOpenedChange }, menuState);
  const dropdownMenu = useDropdownMenu({ items, trigger }, dropdownMenuState);
  const menu = useCustomMenu({ ...menuState, select: dropdownMenu.menuProps.onSelect });

  return { dropdownMenuState, dropdownMenu, menu };
};
